import * as yup from 'yup';

import { AlertRuleTier, TIER_SEVERITY_ORDER } from '../../types';

import { computeTierErrors } from './tierValidation';

export function createValidationSchema(
  isSystemRule: boolean,
  useTiers: boolean,
  independentTiers = false
) {
  const metricTypeSchema = isSystemRule
    ? yup.string().oneOf(['percentage', 'bytes', 'raw', 'days'])
    : yup
        .string()
        .oneOf(['percentage', 'bytes', 'raw', 'days'])
        .required('Metric type is required');

  const baseShape = {
    name: yup.string().required('Name is required'),
    description: yup.string(),
    duration: yup.number().required('Duration is required'),
    enabled: yup.boolean().required(),
    metricType: metricTypeSchema,
  };

  if (useTiers) {
    return yup.object({
      ...baseShape,
      conditionOperator: yup
        .string()
        .required('Condition operator is required')
        .oneOf(['<', '>'], 'Condition operator must be < or > in tier mode'),
      tiers: yup
        .array()
        .length(3, 'Exactly 3 tiers are required')
        .test(
          'tier-severities',
          'Tiers must include info, warning, and critical',
          (tiers) => {
            if (!tiers) {
              return false;
            }

            const severities = new Set(
              (tiers as AlertRuleTier[]).map((t) => t.severity)
            );

            return (
              severities.size === 3 &&
              TIER_SEVERITY_ORDER.every((severity) => severities.has(severity))
            );
          }
        )
        .test(
          'tier-ordering',
          'Tier thresholds are not in valid order',
          function validateTierOrdering(tiers) {
            if (!tiers || tiers.length === 0) return true;
            const op = this.parent.conditionOperator as string;
            const errs = computeTierErrors(
              tiers as AlertRuleTier[],
              op,
              independentTiers
            );
            return Object.keys(errs).length === 0;
          }
        )
        .test(
          'tier-at-least-one',
          'At least one tier must be enabled',
          (tiers) => {
            if (!tiers) return false;
            return (tiers as AlertRuleTier[]).some((t) => t.enabled);
          }
        ),
    });
  }

  return yup.object({
    ...baseShape,
    threshold: yup
      .number()
      .required('Threshold is required')
      .min(0, 'Threshold must be a positive number'),
    conditionOperator: yup.string().required('Condition operator is required'),
    severity: yup.string().required('Severity is required'),
  });
}
